import React, {PropsWithChildren, useCallback, useEffect, useId, useSyncExternalStore} from 'react';
import {createStrictContext} from '~/shared/utils/createStrictContext';
import {DialogsBackdrop} from './DialogsBackdrop';

type CloseCallback = () => Promise<void>;

type DialogRecord = {
    readonly isOpen: boolean;
    readonly onClose: CloseCallback;
};

export type DialogsRegistry = ReturnType<typeof createDialogsRegistry>;

export function createDialogsRegistry() {
    const dialogs = new Map<string, DialogRecord>();
    const subscribers = new Set<() => void>();

    let activeId: string | undefined;

    function subscribe(subscriber: () => void) {
        subscribers.add(subscriber);
        return () => {
            subscribers.delete(subscriber);
        };
    }

    function emit() {
        subscribers.forEach(subscriber => subscriber());
    }

    function firstOpenId() {
        for (const [dialogId, dialog] of dialogs.entries()) {
            if (dialog.isOpen) {
                return dialogId;
            }
        }
        return undefined;
    }

    function register(dialogId: string, isOpen: boolean, onClose: CloseCallback) {
        dialogs.set(dialogId, {isOpen, onClose});
        activeId ??= firstOpenId();
        emit();

        return () => {
            dialogs.delete(dialogId);
            if (dialogId === activeId) {
                activeId = firstOpenId();
            }
            emit();
        };
    }

    function makeTransition(dialogId: string) {
        if (activeId !== dialogId) {
            return;
        }
        activeId = firstOpenId();
        emit();
    }

    function getActiveId() {
        return activeId;
    }

    function hasOpenDialog() {
        return firstOpenId() !== undefined;
    }

    async function closeAll() {
        await Promise.all(
            Array.from(dialogs.values())
                .filter(dialog => dialog.isOpen)
                .map(dialog => dialog.onClose())
        );
    }

    return {
        register,
        makeTransition,
        hasOpenDialog,
        closeAll,
        subscribe,
        getActiveId,
    };
}

const [DialogsRegistryContextProvider, useDialogRegistry] = createStrictContext<DialogsRegistry>();

export function DialogsRegistryProvider({registry, children}: PropsWithChildren<{registry: DialogsRegistry}>) {
    return (
        <DialogsRegistryContextProvider value={registry}>
            {children}
            <DialogsBackdrop registry={registry} />
        </DialogsRegistryContextProvider>
    );
}

export function useDialogsCloser() {
    const registry = useDialogRegistry();
    return {closeAll: registry.closeAll};
}

export function useDialogRegistration(isOpen: boolean, onClose: CloseCallback, dialogId?: string) {
    const registry = useDialogRegistry();
    const componentId = useId();
    dialogId ??= componentId;

    useEffect(() => {
        return registry.register(dialogId, isOpen, onClose);
    }, [registry, dialogId, isOpen, onClose]);

    const isActive = useSyncExternalStore(registry.subscribe, () => registry?.getActiveId() === dialogId);

    const notifyExited = useCallback(() => {
        registry.makeTransition(dialogId);
    }, [registry, dialogId]);

    return {isActive, notifyExited};
}
